import axios from "axios";
import config from "../../../config";
import { getAuthToken } from "../../../utils/authStorage";

/** One palette for the whole overview. Colour is always paired with text and an icon. */
export const PALETTE = {
  green: "#4cceac",   // active
  blue: "#4dabf7",    // attendance
  purple: "#868dfb",  // revenue
  amber: "#f5a623",   // warning / expiring
  red: "#ff6b6b",     // expired / blocked
  grey: "#9e9e9e",
  teal: "#26c6da",
  pink: "#f06292",
};

export const METHOD_META = {
  cash: { label: "Cash", color: PALETTE.green },
  bank_transfer: { label: "Bank transfer", color: PALETTE.blue },
  card: { label: "Card", color: PALETTE.purple },
  online: { label: "Online", color: PALETTE.teal },
  qr_payment: { label: "QR payment", color: PALETTE.pink },
  other: { label: "Other", color: PALETTE.grey },
};
export const FILTER_METHODS = ["cash", "bank_transfer", "card"];

export const CATEGORY_META = {
  individual: { label: "Individual", color: PALETTE.blue },
  couple: { label: "Couple", color: PALETTE.pink },
  family: { label: "Family", color: PALETTE.green },
  founding: { label: "Founding", color: PALETTE.amber },
};

export const RANGE_PRESETS = [
  { value: "today", label: "Today" },
  { value: "last7", label: "Last 7 days" },
  { value: "last30", label: "Last 30 days" },
  { value: "this_month", label: "This month" },
  { value: "last_month", label: "Last month" },
  { value: "last6m", label: "Last 6 months" },
  { value: "last12m", label: "Last 12 months" },
  { value: "custom", label: "Custom" },
];
export const DEFAULT_PRESET = "last30";
export const MAX_RANGE_DAYS = 366;

const ISO_RE = /^\d{4}-\d{2}-\d{2}$/;

/** Today's calendar date in Sri Lanka (YYYY-MM-DD), whatever the browser time zone is. */
export const sriLankaToday = () =>
  new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Colombo", year: "numeric", month: "2-digit", day: "2-digit" })
    .format(new Date());

// Calendar arithmetic in UTC so results never shift with the browser time zone.
const toDate = (iso) => new Date(`${iso}T00:00:00Z`);
const toIso = (d) => d.toISOString().slice(0, 10);
export const addDays = (iso, days) => {
  const d = toDate(iso);
  d.setUTCDate(d.getUTCDate() + days);
  return toIso(d);
};
const monthStart = (iso, monthsBack = 0) => {
  const d = toDate(iso);
  return toIso(new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() - monthsBack, 1)));
};
export const daysBetween = (from, to) => Math.round((toDate(to) - toDate(from)) / 86400000) + 1;
const isValidIso = (value) => ISO_RE.test(value || "") && !Number.isNaN(toDate(value).getTime())
  && toIso(toDate(value)) === value;

export const presetRange = (preset, today) => {
  switch (preset) {
    case "today": return { from: today, to: today };
    case "last7": return { from: addDays(today, -6), to: today };
    case "this_month": return { from: monthStart(today), to: today };
    case "last_month": return { from: monthStart(today, 1), to: addDays(monthStart(today), -1) };
    case "last6m": return { from: monthStart(today, 5), to: today };
    case "last12m": return { from: monthStart(today, 11), to: today };
    case "last30":
    default: return { from: addDays(today, -29), to: today };
  }
};

/** Problem with a custom range, or "" when it is valid. Same rules as the server. */
export const customRangeError = (from, to, today) => {
  if (!isValidIso(from) || !isValidIso(to)) return "Choose both dates.";
  if (from > to) return "The start date must not be after the end date.";
  if (to > today) return "The end date cannot be in the future.";
  if (daysBetween(from, to) > MAX_RANGE_DAYS) return `The range can be at most ${MAX_RANGE_DAYS} days.`;
  return "";
};

/** Range from the URL (?range=…&from=…&to=…); invalid values fall back to the default. */
export const rangeFromParams = (params, today) => {
  const preset = RANGE_PRESETS.some((p) => p.value === params.get("range")) ? params.get("range") : DEFAULT_PRESET;
  if (preset === "custom") {
    const from = params.get("from");
    const to = params.get("to");
    if (!customRangeError(from, to, today)) return { preset, from, to };
    return { preset: DEFAULT_PRESET, ...presetRange(DEFAULT_PRESET, today) };
  }
  return { preset, ...presetRange(preset, today) };
};

// ---------------------------------------------------------------- formatting
const fmt = (iso, options) =>
  iso ? new Intl.DateTimeFormat("en-LK", { ...options, timeZone: "UTC" }).format(toDate(iso)) : "-";

export const shortDate = (iso) => fmt(iso, { day: "numeric", month: "short" });
export const longDate = (iso) => fmt(iso, { day: "numeric", month: "short", year: "numeric" });
export const weekdayDate = (iso) => fmt(iso, { weekday: "short", day: "numeric", month: "short" });
export const monthLabel = (yyyyMm) => (yyyyMm ? fmt(`${yyyyMm}-01`, { month: "short", year: "2-digit" }) : "-");
export const longMonth = (yyyyMm) => (yyyyMm ? fmt(`${yyyyMm}-01`, { month: "long", year: "numeric" }) : "-");

/** "1 Oct and 27 Oct" (years shown when the range is not in the current year). */
export const rangeText = (from, to, today = sriLankaToday()) => {
  if (!from || !to) return "the selected dates";
  const sameYear = from.slice(0, 4) === to.slice(0, 4) && to.slice(0, 4) === today.slice(0, 4);
  const f = sameYear ? shortDate : longDate;
  return from === to ? f(from) : `${f(from)} and ${f(to)}`;
};

/** Short name of the chosen range for KPI titles: "Last 7 days", or "5 Oct – 25 Oct" for a custom range. */
export const periodLabel = (preset, from, to, today = sriLankaToday()) => {
  const named = RANGE_PRESETS.find((p) => p.value === preset && p.value !== "custom");
  if (named) return named.label;
  if (!from || !to) return "Selected dates";
  const sameYear = from.slice(0, 4) === to.slice(0, 4) && to.slice(0, 4) === today.slice(0, 4);
  const f = sameYear ? shortDate : longDate;
  return from === to ? f(from) : `${f(from)} – ${f(to)}`;
};

/** Comparison chip text for a range KPI (``section`` carries previous_from / previous_to from the server). */
export const compareLabel = (preset, section) => {
  switch (preset) {
    case "this_month": return "vs same days last month";
    case "last_month": return section?.previous_from ? `vs ${longMonth(section.previous_from.slice(0, 7))}` : "vs the month before";
    case "last6m": return "vs previous 6 months";
    case "last12m": return "vs previous 12 months";
    default: {
      if (!section?.previous_from || !section?.previous_to) return "vs previous period";
      const days = daysBetween(section.previous_from, section.previous_to);
      return days === 1 ? `vs ${weekdayDate(section.previous_from)}` : `vs previous ${days} days`;
    }
  }
};

export const formatLkr = (value, decimals = 0) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "LKR 0";
  return `LKR ${n.toLocaleString("en-LK", { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}`;
};

export const compactNumber = (value) => {
  const n = Number(value) || 0;
  if (Math.abs(n) >= 1000000) return `${(n / 1000000).toFixed(1).replace(/\.0$/, "")}M`;
  if (Math.abs(n) >= 1000) return `${(n / 1000).toFixed(1).replace(/\.0$/, "")}k`;
  return String(n);
};

export const plural = (n, word, many = `${word}s`) => `${n} ${n === 1 ? word : many}`;

export const titleCase = (value) =>
  (value || "").split("_").filter(Boolean).map((p) => p.charAt(0).toUpperCase() + p.slice(1)).join(" ") || "-";

export const timeOf = (localDateTime) => (localDateTime ? localDateTime.slice(11, 16) : "-");

// ---------------------------------------------------------------- API
const authHeaders = () => {
  const token = getAuthToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/** GET /hpc/admin/dashboard/overview{path}; empty params are dropped. */
export const overviewApi = async (path, params = {}) => {
  const clean = Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ""));
  const res = await axios.get(`${config.apiBaseUrl}/hpc/admin/dashboard/overview${path}`, {
    params: clean,
    headers: authHeaders(),
  });
  return res.data;
};

/** Downloads the Operations Report PDF for the range (same blob pattern as the member export). */
export const downloadMonthlyReport = async (from, to) => {
  try {
    const response = await axios.get(`${config.apiBaseUrl}/hpc/admin/reports/monthly`, {
      params: { from, to },
      headers: authHeaders(),
      responseType: "blob",
    });
    const disposition = response.headers["content-disposition"] || "";
    const filenameMatch = disposition.match(/filename="?([^";]+)"?/i);
    const blob = new Blob([response.data], { type: response.headers["content-type"] || "application/pdf" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filenameMatch ? filenameMatch[1] : `TEBA_HPU_Report_${from}_${to}.pdf`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (error) {
    // Blob error bodies carry the JSON message from the server.
    if (error?.response?.data instanceof Blob) {
      try {
        const body = JSON.parse(await error.response.data.text());
        error.response.data = body;
      } catch {
        // keep the generic message below
      }
    }
    throw error;
  }
};

export const requestError = (error, fallback) => {
  const status = error?.response?.status;
  if (status === 401) return "Your session has expired. Please log in again.";
  if (status === 403) return "You do not have access to the HPU overview.";
  return error?.response?.data?.error || fallback;
};
